import { queryOptions, useMutation, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import PurchaserClient from "../PurchaserClient";
import { queryClient, type QueryInvalidateOption } from "./client";

const FIVE_SECONDS = 5 * 1000;

/**
 * Awards Query Options
 */
export const AwardsQueryOptions = {
    RFQAwards(rfqPublicId?: string | null) {
        return queryOptions({
            queryKey: ["rfqAwards", rfqPublicId],
            queryFn: async () => {
                if (!rfqPublicId) throw new Error("Invalid RFQ Public ID");
                return await PurchaserClient.getRFQAwards(rfqPublicId);
            },
            refetchInterval: FIVE_SECONDS,
            staleTime: FIVE_SECONDS,
            enabled: !!rfqPublicId,
        });
    },
};

/**
 * Awards Hooks
 */
export const useRFQAwards = (rfqPublicId?: string | null) => {
    const query = useQuery(AwardsQueryOptions.RFQAwards(rfqPublicId));
    const { data, ...rest } = query;

    const awards = useMemo(() => data ?? [], [data]);

    return { awards, ...rest };
};

/**
 * Awards Invalidation Functions
 */
export async function invalidateRFQAwardsQueries(params?: { rfqPublicId?: string; option?: QueryInvalidateOption }) {
    const { rfqPublicId, option } = params ?? {};

    const queryKey: string[] = ["rfqAwards"];
    if (rfqPublicId) queryKey.push(rfqPublicId);

    if (option === "Remove") {
        await queryClient.removeQueries({ queryKey });
    } else {
        await queryClient.invalidateQueries({ queryKey });
    }
}

/**
 * Awards Mutations
 */
export const useDeleteAward = () => {
    const mutation = useMutation({
        mutationFn: async (awardId: number) => {
            await PurchaserClient.deleteAward(awardId);
        },
        async onSuccess() {
            await invalidateRFQAwardsQueries();
        },
        onError(e) {
            console.error("Error deleting award", e);
        },
    });

    return {
        deleteAward: mutation.mutate,
        isDeletingAward: mutation.isPending,
    };
};

export const useUpdateAwardRationale = () => {
    const mutation = useMutation({
        mutationFn: async (params: { awardId: number; rationale: string }) => {
            await PurchaserClient.updateAwardRationale(params.awardId, params.rationale);
        },
        async onSuccess() {
            await invalidateRFQAwardsQueries();
        },
        onError(e) {
            console.error("Error updating award rationale", e);
        },
    });

    return {
        updateAwardRationale: mutation.mutate,
        isUpdatingAwardRationale: mutation.isPending,
    };
};

export const useCreateAward = () => {
    const mutation = useMutation({
        mutationFn: PurchaserClient.createAward,
        async onSuccess() {
            await invalidateRFQAwardsQueries();
        },
        onError(e) {
            console.error("Error creating award", e);
        },
    });

    return {
        createAward: mutation.mutateAsync,
        isCreatingAward: mutation.isPending,
    };
};

export const useUpdateAward = () => {
    const mutation = useMutation({
        mutationFn: PurchaserClient.updateAward,
        async onSuccess() {
            await invalidateRFQAwardsQueries();
        },
        onError(e) {
            console.error("Error updating award", e);
        },
    });

    return {
        updateAward: mutation.mutateAsync,
        isUpdatingAward: mutation.isPending,
    };
};
